import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { menuItems, contactInfo } from '../data';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isUnder1200, setIsUnder1200] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpenMenus, setMobileOpenMenus] = useState({});
  const location = useLocation();

  const isPathActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname === href;
  };

  const isItemActive = (item) => {
    return isPathActive(item.href);
  };

  const isParentActive = (item) => {
    if (!item.children) return false;
    return item.children.some(child => {
      if (isItemActive(child)) return true;
      if (child.children) {
        return child.children.some(nested => isItemActive(nested));
      }
      return false;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsUnder1200(window.innerWidth < 1200);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    setMobileOpenMenus({});
  }, [location.pathname]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const toggleMobileMenu = (label) => {
    setMobileOpenMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-200/80 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img 
            src="https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/logo.png" 
            alt="Munkgaard Træbyg ApS" 
            className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {!isUnder1200 && (
          <nav className="flex items-center gap-8">
            {menuItems.map((item) => (
              <div 
                key={item.label} 
                className="relative group"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {item.children ? (
                  <div className="relative">
                    <NavLink 
                      to={item.href}
                      className={() => {
                        const active = isItemActive(item);
                        const parentActive = isParentActive(item);
                        return `flex items-center gap-1.5 py-2 text-sm font-medium transition-colors hover:text-accent cursor-pointer ${
                          active || parentActive ? 'text-accent font-semibold' : 'text-stone-700'
                        }`;
                      }}
                    >
                      {item.label}
                      <i className="fa-solid fa-chevron-down text-[10px] transition-transform duration-300 group-hover:rotate-180" />
                    </NavLink>
                    {(isItemActive(item) || isParentActive(item)) && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                    )}
                  </div>
                ) : (
                  <NavLink 
                    to={item.href}
                    className={({ isActive }) => 
                      `relative py-2 text-sm font-medium transition-colors hover:text-accent ${
                        isActive ? 'text-accent font-semibold' : 'text-stone-700'
                      }`
                    }
                  >
                    {item.label}
                    {location.pathname === item.href && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent rounded-full" />
                    )}
                  </NavLink>
                )}

                {/* Dropdown Level 1 */}
                {item.children && activeDropdown === item.label && (
                  <div className="absolute left-0 top-full pt-2 w-64 animate-fade-in">
                    <div className="bg-white border border-stone-200 rounded-lg shadow-xl py-3 px-2 flex flex-col gap-1">
                      {item.children.map((subItem) => {
                        const hasSubChildren = subItem.children && subItem.children.length > 0;
                        const subActive = isItemActive(subItem) || isParentActive(subItem);
                        return (
                          <div 
                            key={subItem.label} 
                            className="relative group/sub"
                          >
                            {hasSubChildren ? (
                              <div className="relative">
                                <div className={`flex items-center justify-between px-4 py-2.5 text-sm rounded-md hover:bg-stone-50 hover:text-accent transition-all duration-200 cursor-pointer ${
                                  subActive ? 'bg-stone-50 text-accent font-medium' : 'text-stone-600'
                                }`}>
                                  <Link to={subItem.href} className="flex-grow font-medium">
                                    {subItem.label}
                                  </Link>
                                  <i className="fa-solid fa-chevron-right text-[10px] ml-2 transition-transform duration-200 group-hover/sub:translate-x-0.5" />
                                </div>

                                {/* Dropdown Level 2 (Flyout to the right) */}
                                <div className="absolute left-full top-0 pl-2 w-64 hidden group-hover/sub:block animate-fade-in">
                                  <div className="bg-white border border-stone-200 rounded-lg shadow-xl py-3 px-2 flex flex-col gap-1">
                                    {subItem.children.map((nestedItem) => (
                                      <NavLink
                                        key={nestedItem.label}
                                        to={nestedItem.href}
                                        className={({ isActive }) => 
                                          `px-4 py-2.5 text-sm rounded-md transition-all duration-200 hover:bg-stone-50 hover:text-accent ${
                                            isActive ? 'bg-stone-50 text-accent font-medium' : 'text-stone-600'
                                          }`
                                        }
                                      >
                                        {nestedItem.label}
                                      </NavLink>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <NavLink
                                to={subItem.href}
                                className={({ isActive }) => 
                                  `block px-4 py-2.5 text-sm rounded-md transition-all duration-200 hover:bg-stone-50 hover:text-accent ${
                                    isActive ? 'bg-stone-50 text-accent font-medium' : 'text-stone-600'
                                  }`
                                }
                              >
                                {subItem.label}
                              </NavLink>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* CTA Button */}
            <Link 
              to="/kontakt" 
              className="bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-md text-sm font-medium tracking-wide transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              Få et tilbud
            </Link>
          </nav>
        )}

        {/* Mobile Menu Button (< 1200px) */}
        {isUnder1200 && (
          <button 
            onClick={toggleMenu}
            className="p-2 text-stone-700 hover:text-accent transition-colors focus:outline-none cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
          </button>
        )}
      </div>

      {/* Mobile Navigation Drawer (< 1200px) */}
      {isUnder1200 && isOpen && (
        <div className="fixed inset-0 top-24 z-40 bg-stone-900/40 backdrop-blur-sm animate-fade-in">
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl p-8 flex flex-col justify-between overflow-y-auto">
            <div className="flex flex-col gap-4">
              {menuItems.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isMenuOpen = mobileOpenMenus[item.label];

                return (
                  <div key={item.label} className="border-b border-stone-100 pb-3">
                    {hasChildren ? (
                      <div className="flex flex-col">
                        <button 
                          onClick={() => toggleMobileMenu(item.label)}
                          className="flex items-center justify-between w-full py-2 text-lg font-semibold text-stone-800 hover:text-accent transition-colors text-left cursor-pointer"
                        >
                          <span>{item.label}</span>
                          <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-300 ${isMenuOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isMenuOpen && (
                          <div className="flex flex-col gap-2 pl-4 mt-2 border-l border-stone-100 animate-fade-in">
                            {/* Main overview link */}
                            <NavLink
                              to={item.href}
                              end
                              className={({ isActive }) => 
                                `text-base font-medium transition-colors hover:text-accent py-1.5 ${
                                  isActive ? 'text-accent font-semibold' : 'text-stone-600'
                                }`
                              }
                            >
                              {item.label} (Oversigt)
                            </NavLink>
                            
                            {item.children.map((subItem) => {
                              const hasSubChildren = subItem.children && subItem.children.length > 0;
                              const isSubOpen = mobileOpenMenus[`${item.label}-${subItem.label}`];
                              
                              if (hasSubChildren) {
                                return (
                                  <div key={subItem.label} className="flex flex-col py-1">
                                    <button
                                      onClick={() => toggleMobileMenu(`${item.label}-${subItem.label}`)}
                                      className="flex items-center justify-between w-full py-1.5 text-base font-medium text-stone-700 hover:text-accent transition-colors text-left cursor-pointer"
                                    >
                                      <span>{subItem.label}</span>
                                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-300 ${isSubOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    {isSubOpen && (
                                      <div className="flex flex-col gap-1.5 pl-4 mt-1 border-l border-stone-100 animate-fade-in">
                                        {subItem.children.map((nestedItem) => (
                                          <NavLink
                                            key={nestedItem.label}
                                            to={nestedItem.href}
                                            className={({ isActive }) => 
                                              `text-sm font-medium transition-colors hover:text-accent py-1 ${
                                                isActive ? 'text-accent font-semibold' : 'text-stone-500'
                                              }`
                                            }
                                          >
                                            {nestedItem.label}
                                          </NavLink>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              
                              return (
                                <NavLink
                                  key={subItem.label}
                                  to={subItem.href}
                                  className={({ isActive }) => 
                                    `text-base font-medium transition-colors hover:text-accent py-1.5 ${
                                      isActive ? 'text-accent font-semibold' : 'text-stone-700'
                                    }`
                                  }
                                >
                                  {subItem.label}
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <NavLink
                        to={item.href}
                        className={({ isActive }) => 
                          `text-lg font-semibold transition-colors hover:text-accent block py-2 ${
                            isActive ? 'text-accent' : 'text-stone-800'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Footer Info */}
            <div className="mt-12 pt-6 border-t border-stone-200 flex flex-col gap-4">
              <a 
                href={`tel:${contactInfo.phoneRaw}`} 
                className="flex items-center gap-3 text-stone-700 hover:text-accent transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-accent" />
                {contactInfo.phone}
              </a>
              <a 
                href={`mailto:${contactInfo.email}`} 
                className="flex items-center gap-3 text-stone-700 hover:text-accent transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-accent" />
                {contactInfo.email}
              </a>
              <Link 
                to="/kontakt" 
                className="bg-primary hover:bg-primary-light text-white text-center py-3.5 rounded-md text-sm font-medium tracking-wide transition-all duration-300 mt-2"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
